import React, { useCallback, useMemo } from 'react'
import DivideCarFourSymbol from 'components/elements/symbols/DamagedPart/DivideCarFourSymbol'

import styled from 'styled-components'
import { VehiclePart } from '../../../../states/QnState'
import ItemLine from '../ItemLine'
import CheckItem from './CheckItem'

/** FourWheeledVehicleLineのpropsのtype定義 */
type DivideCarLineProps = {
  /** 選択中の破損個所 */
  selectedParts: Array<VehiclePart>
  /** チェック選択時のコールバック */
  toggleDamagedPart?: (value: VehiclePart) => void
}

/**
 * 四輪自動車の破損個所選択行
 * @param props DivideCarLineProps
 * @returns
 */
const FourWheeledVehicleLine: React.FC<DivideCarLineProps> = (props) => {
  const { selectedParts, toggleDamagedPart } = props
  /**
   * 破損個所を押下したときのクリックイベント処理
   * @param value 破損箇所の場所
   */
  const handleCheckClick = useCallback(
    (value: VehiclePart) => {
      if (!toggleDamagedPart) {
        return
      }
      toggleDamagedPart(value)
    },
    [selectedParts]
  )

  /** 各タイヤの破損状態の初期値 */
  const initialPartsStatus: Record<VehiclePart, boolean> = {
    front: false,
    rear: false,
    leftFront: false,
    rightFront: false,
    rightRear: false,
    leftRear: false,
    allFront: false,
    allRear: false,
  }

  /** 各タイヤの破損状態をフラグ値で持つ */
  const partsStatus: Record<VehiclePart, boolean> = useMemo(() => {
    const newDamage =
      selectedParts.length > 0 &&
      selectedParts.map((parts) => {
        return { [parts]: true }
      })
    return newDamage
      ? Object.assign({}, initialPartsStatus, ...newDamage)
      : initialPartsStatus
  }, [selectedParts])

  return (
    <ItemLine variableHeight showLineBorder>
      <InnerContainer>
        <SymbolArea>
          <DivideCarFourSymbol />
        </SymbolArea>
        <CheckOverlayArea>
          <AllCheckOverlayWrapper />
          <CenterCheckOverlayWrapper>
            <HalfArea>
              <BoxOverlay isDamaged={partsStatus.rightFront} />
              <BoxOverlay isDamaged={partsStatus.leftFront} />
            </HalfArea>
            <HalfArea>
              <BoxOverlay isDamaged={partsStatus.rightRear} />
              <BoxOverlay isDamaged={partsStatus.leftRear} />
            </HalfArea>
          </CenterCheckOverlayWrapper>
          <AllCheckOverlayWrapper />
        </CheckOverlayArea>
        <CheckOverlayArea>
          <AllCheckOverlayWrapper>
            <CheckItem
              isChecked={partsStatus.allFront}
              onClick={(): void => {
                handleCheckClick('allFront')
              }}
              marginLeft="10%"
              marginRight="auto"
            />
          </AllCheckOverlayWrapper>
          <CenterCheckOverlayWrapper>
            <LeftRightArea>
              <CheckOverlayWrapper>
                <CheckItem
                  isChecked={partsStatus.rightFront}
                  onClick={(): void => {
                    handleCheckClick('rightFront')
                  }}
                  marginTop="5%"
                  marginRight="auto"
                  marginBottom="auto"
                  marginLeft="25%"
                />
              </CheckOverlayWrapper>
              <CheckOverlayWrapper isLeft={true}>
                <CheckItem
                  isChecked={partsStatus.leftFront}
                  onClick={(): void => {
                    handleCheckClick('leftFront')
                  }}
                  marginTop="auto"
                  marginRight="auto"
                  marginBottom="10%"
                  marginLeft="25%"
                />
              </CheckOverlayWrapper>
            </LeftRightArea>
            <LeftRightArea>
              <CheckOverlayWrapper isBack={true}>
                <CheckItem
                  isChecked={partsStatus.rightRear}
                  onClick={(): void => {
                    handleCheckClick('rightRear')
                  }}
                  marginTop="5%"
                  marginRight="25%"
                  marginBottom="auto"
                  marginLeft="auto"
                />
              </CheckOverlayWrapper>
              <CheckOverlayWrapper isBack={true} isLeft={true}>
                <CheckItem
                  isChecked={partsStatus.leftRear}
                  onClick={(): void => {
                    handleCheckClick('leftRear')
                  }}
                  marginTop="auto"
                  marginRight="25%"
                  marginBottom="10%"
                  marginLeft="auto"
                />
              </CheckOverlayWrapper>
            </LeftRightArea>
          </CenterCheckOverlayWrapper>
          <AllCheckOverlayWrapper isBack={true}>
            <CheckItem
              isChecked={partsStatus.allRear}
              onClick={(): void => {
                handleCheckClick('allRear')
              }}
              marginLeft="auto"
              marginRight="10%"
            />
          </AllCheckOverlayWrapper>
        </CheckOverlayArea>
      </InnerContainer>
    </ItemLine>
  )
}

export default FourWheeledVehicleLine

const InnerContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 640px;
  height: 100%;
  margin: 0 auto;
`

const SymbolArea = styled.div`
  margin: 32px 16px;
`
const CheckOverlayArea = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  bottom: 16px;
  left: 16px;
  display: flex;
  flex-direction: row;
`

const BoxOverlay = styled.div<{
  isDamaged: boolean
  isLeft?: boolean
}>`
  width: 100%;
  height: ${(props): string => (props.isLeft ? '52%' : '48%')};
  background: ${(props): string =>
    props.isDamaged ? 'var(--color-main)' : 'transparent'};
  opacity: 0.16;
`

const HalfArea = styled.div`
  width: 50%;
  height: 100%;
`

const AllCheckOverlayWrapper = styled.div<{ isBack?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${(props): string =>
    props.isBack ? 'flex-end' : 'flex-start'};
  width: 10%;
  height: 100%;
`
const CenterCheckOverlayWrapper = styled.div`
  display: flex;
  flex: 1;
`

const LeftRightArea = styled.div`
  width: 100%;
  height: 100%;
`

const CheckOverlayWrapper = styled.div<{ isBack?: boolean; isLeft?: boolean }>`
  display: flex;
  align-items: ${(props): string => (props.isLeft ? 'flex-end' : 'flex-start')};
  justify-content: ${(props): string =>
    props.isBack ? 'flex-end' : 'flex-start'};
  width: 100%;
  height: 50%;
`
